package br.com.htms.inspeccaocampo.ui.screens

import br.com.htms.inspeccaocampo.ui.kit.CartaoSecao
import br.com.htms.inspeccaocampo.ui.kit.PortalOutlinedButton
import br.com.htms.inspeccaocampo.ui.kit.PortalButton
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.apr.AprAssinatura
import br.com.htms.inspeccaocampo.data.apr.AprCatalog
import br.com.htms.inspeccaocampo.data.apr.AprDados
import br.com.htms.inspeccaocampo.data.apr.AprEtapa
import br.com.htms.inspeccaocampo.data.apr.AprExecutante
import br.com.htms.inspeccaocampo.data.apr.AprPessoa
import br.com.htms.inspeccaocampo.data.apr.AprSecao
import br.com.htms.inspeccaocampo.data.funcionarios.Funcionario
import br.com.htms.inspeccaocampo.data.funcionarios.FuncionariosApi
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.form.AppTab
import br.com.htms.inspeccaocampo.shared.ui.components.AppDateTextField
import br.com.htms.inspeccaocampo.shared.ui.components.AppOutlinedTextField
import br.com.htms.inspeccaocampo.shared.ui.components.DateFieldKind
import br.com.htms.inspeccaocampo.shared.ui.components.DateInputMask
import br.com.htms.inspeccaocampo.shared.ui.components.FormFieldDefaults

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun SharedAprScreen(controller: AppController) {
    val d by controller.aprRascunho.collectAsState()
    val gerando by controller.gerandoApr.collectAsState()
    val versaoDocs by controller.documentosVersao.collectAsState()
    var funcionarios by remember { mutableStateOf<List<Funcionario>>(emptyList()) }
    var tentouGerar by remember { mutableStateOf(false) }
    var confirmarLimpar by remember { mutableStateOf(false) }
    fun up(f: (AprDados) -> AprDados) = controller.atualizarApr(f)

    LaunchedEffect(versaoDocs) {
        FuncionariosApi.listar().onSuccess { funcionarios = it }
    }

    val emissaoOk = DateInputMask.isCompleteValid(d.emissao, DateFieldKind.DATE)
    val vigenciaOk = d.vigencia.isBlank() || DateInputMask.isCompleteValid(d.vigencia, DateFieldKind.DATE)
    val atividadeOk = d.atividade.isNotBlank()
    val podeGerar = emissaoOk && vigenciaOk && atividadeOk && !gerando

    DocumentosModulo(controller, AppTab.APR, "APR - Análise Preliminar de Riscos") {
        Text(
            "Preencha os campos do modelo. Responsáveis, assinantes e executantes podem ser buscados no cadastro de " +
                "funcionários (nome e matrícula); EPIs e EPCs vêm da Matriz de EPI e EPC. O preenchimento fica guardado " +
                "enquanto o portal estiver aberto.",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        Secao("Identificação da atividade") {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                Campo("Nº APR", d.numeroApr, Modifier.weight(1f)) { v -> up { it.copy(numeroApr = v) } }
                Campo("Nº SI/OS", d.numeroSiOs, Modifier.weight(1f)) { v -> up { it.copy(numeroSiOs = v) } }
            }
            Campo(
                "Atividade a ser realizada conforme planejamento *", d.atividade, linhas = 3,
                erro = tentouGerar && !atividadeOk
            ) { v -> up { it.copy(atividade = v) } }
            Campo("Instalação", d.instalacao, linhas = 2) { v -> up { it.copy(instalacao = v) } }
            Campo("Local/equipamento", d.local, linhas = 2) { v -> up { it.copy(local = v) } }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.weight(1f)) {
                    AppDateTextField(value = d.emissao, onValueChange = { v -> up { it.copy(emissao = v) } }, label = "Emissão", isError = !emissaoOk)
                }
                Campo("Hora", d.hora, Modifier.weight(0.7f)) { v -> up { it.copy(hora = v) } }
                Column(Modifier.weight(1f)) {
                    AppDateTextField(value = d.vigencia, onValueChange = { v -> up { it.copy(vigencia = v) } }, label = "Vigência", isError = !vigenciaOk)
                }
            }
            CampoColaborador("Responsável", d.responsavel, funcionarios, { v -> up { it.copy(responsavel = v) } }) { f ->
                up { it.copy(responsavel = AprCatalog.rotuloPessoa(f)) }
            }
            Campo("Responsáveis substitutos", d.substitutos, linhas = 2) { v -> up { it.copy(substitutos = v) } }
            BuscaParaAdicionar("Buscar colaborador para adicionar como substituto", funcionarios) { f ->
                up { it.copy(substitutos = AprCatalog.adicionar(it.substitutos, AprCatalog.rotuloPessoa(f))) }
            }
            Campo("Máquinas e equipamentos", d.maquinas, linhas = 2) { v -> up { it.copy(maquinas = v) } }
        }

        Secao("EPIs e EPCs (Matriz de EPI e EPC)") {
            val executantesCadastrados = d.executantes.mapNotNull { ex ->
                funcionarios.firstOrNull { it.nome.equals(ex.nome.trim(), ignoreCase = true) }
            }
            PortalOutlinedButton(
                enabled = executantesCadastrados.isNotEmpty(),
                onClick = {
                    val (obrig, event) = AprCatalog.sugerirEpis(executantesCadastrados)
                    up { atual ->
                        atual.copy(
                            episObrigatorios = obrig.fold(atual.episObrigatorios) { t, i -> AprCatalog.adicionar(t, i) },
                            episEventuais = event.fold(atual.episEventuais) { t, i -> AprCatalog.adicionar(t, i) }
                        )
                    }
                }
            ) { Text("Sugerir EPIs pela Matriz (cargos dos executantes)") }
            if (executantesCadastrados.isEmpty()) {
                Text(
                    "Adicione executantes do cadastro para sugerir os EPIs pela função de cada um.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            ListaItens("EPI's obrigatórios", d.episObrigatorios, AprCatalog.epis) { v -> up { it.copy(episObrigatorios = v) } }
            ListaItens("EPI's eventuais", d.episEventuais, AprCatalog.epis) { v -> up { it.copy(episEventuais = v) } }
            ListaItens("EPC's obrigatórios", d.epcsObrigatorios, AprCatalog.epcs) { v -> up { it.copy(epcsObrigatorios = v) } }
            ListaItens("EPC's eventuais", d.epcsEventuais, AprCatalog.epcs) { v -> up { it.copy(epcsEventuais = v) } }
        }

        Secao("Descrição da técnica / definição de responsabilidades") {
            d.etapas.forEachIndexed { i, e ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                ) {
                    Column(Modifier.padding(10.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text("Item ${i + 1}", fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                            if (d.etapas.size > 1) {
                                TextButton(onClick = { up { it.copy(etapas = it.etapas.filterIndexed { j, _ -> j != i }) } }) {
                                    Text("Remover")
                                }
                            }
                        }
                        fun upEtapa(f: (AprEtapa) -> AprEtapa) =
                            up { it.copy(etapas = it.etapas.mapIndexed { j, x -> if (j == i) f(x) else x }) }
                        Campo("Item do PEX", e.itemPex, linhas = 2) { v -> upEtapa { it.copy(itemPex = v) } }
                        Campo("Etapas da tarefa (passo a passo)", e.etapas, linhas = 3) { v -> upEtapa { it.copy(etapas = v) } }
                        Campo("Perigos e riscos", e.perigos, linhas = 3) { v -> upEtapa { it.copy(perigos = v) } }
                        Campo("PAC", e.pac) { v -> upEtapa { it.copy(pac = v) } }
                        Campo("Medidas de controle", e.medidas, linhas = 3) { v -> upEtapa { it.copy(medidas = v) } }
                        Campo("O que pode dar errado", e.oQuePodeDarErrado, linhas = 2) { v -> upEtapa { it.copy(oQuePodeDarErrado = v) } }
                        Campo("Medidas de proteção adicional", e.medidasAdicionais, linhas = 2) { v -> upEtapa { it.copy(medidasAdicionais = v) } }
                    }
                }
            }
            PortalOutlinedButton(onClick = { up { it.copy(etapas = it.etapas + AprEtapa()) } }) { Text("+ Adicionar item") }
            Campo("Observações complementares", d.observacoes, linhas = 3) { v -> up { it.copy(observacoes = v) } }
        }

        AprSecao.entries.forEach { secao ->
            Secao(secao.titulo.substringBefore(" (").lowercase().replaceFirstChar { it.uppercase() }) {
                AprAssinatura.entries.filter { it.secao == secao }.forEach { a ->
                    val p = d.pessoa(a)
                    fun upPessoa(nova: AprPessoa) = up { it.copy(assinaturas = it.assinaturas + (a to nova)) }
                    Text(a.rotulo, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.SemiBold)
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        CampoColaborador(
                            "Nome", p.nome, funcionarios, { v -> upPessoa(p.copy(nome = v)) }, Modifier.weight(2f)
                        ) { f -> upPessoa(AprPessoa(f.nome, f.matricula)) }
                        Campo("Matrícula", p.matricula, Modifier.weight(1f)) { v -> upPessoa(p.copy(matricula = v)) }
                    }
                }
            }
        }

        Secao("Relação dos executantes da tarefa") {
            d.executantes.forEachIndexed { i, ex ->
                fun upEx(f: (AprExecutante) -> AprExecutante) =
                    up { it.copy(executantes = it.executantes.mapIndexed { j, x -> if (j == i) f(x) else x }) }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth()) {
                    Text("${i + 1}.", modifier = Modifier.padding(top = 18.dp))
                    CampoColaborador("Nome", ex.nome, funcionarios, { v -> upEx { it.copy(nome = v) } }, Modifier.weight(2f)) { f ->
                        upEx { it.copy(nome = f.nome) }
                    }
                    Campo("Empresa", ex.empresa, Modifier.weight(1f)) { v -> upEx { it.copy(empresa = v) } }
                    TextButton(
                        onClick = { up { it.copy(executantes = it.executantes.filterIndexed { j, _ -> j != i }) } },
                        modifier = Modifier.padding(top = 8.dp)
                    ) { Text("X") }
                }
            }
            BuscaParaAdicionar("Buscar colaborador para adicionar como executante", funcionarios) { f ->
                up {
                    if (it.executantes.any { x -> x.nome.equals(f.nome, ignoreCase = true) }) it
                    else it.copy(executantes = it.executantes + AprExecutante(f.nome))
                }
            }
            PortalOutlinedButton(onClick = { up { it.copy(executantes = it.executantes + AprExecutante()) } }) {
                Text("+ Executante não cadastrado")
            }
            Text(
                "Linhas em branco até completar 17 saem no documento para assinatura manual.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        if (tentouGerar && !podeGerar && !gerando) {
            Text(
                "Preencha a atividade e confira as datas de emissão e vigência (dd/mm/aaaa).",
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            PortalButton(
                onClick = { tentouGerar = true; if (podeGerar) controller.gerarApr(excel = false) },
                enabled = !gerando,
                modifier = Modifier.weight(1f)
            ) {
                if (gerando) CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                else Text("Emitir PDF (horizontal)")
            }
            PortalButton(
                onClick = { tentouGerar = true; if (podeGerar) controller.gerarApr(excel = true) },
                enabled = !gerando,
                modifier = Modifier.weight(1f)
            ) {
                if (gerando) CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                else Text("Emitir Excel")
            }
        }
        if (confirmarLimpar) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                Text("Apagar todo o preenchimento?", modifier = Modifier.weight(1f))
                TextButton(onClick = { controller.limparApr(); confirmarLimpar = false; tentouGerar = false }) { Text("Sim, limpar") }
                TextButton(onClick = { confirmarLimpar = false }) { Text("Cancelar") }
            }
        } else {
            TextButton(onClick = { confirmarLimpar = true }) { Text("Nova APR (limpar campos)") }
        }
    }
}

@Composable
private fun Secao(titulo: String, conteudo: @Composable ColumnScope.() -> Unit) {
    CartaoSecao(titulo, content = conteudo)
}

@Composable
private fun Campo(
    rotulo: String,
    valor: String,
    modifier: Modifier = Modifier.fillMaxWidth(),
    linhas: Int = 1,
    erro: Boolean = false,
    onValor: (String) -> Unit
) {
    AppOutlinedTextField(
        value = valor,
        onValueChange = onValor,
        label = { Text(rotulo) },
        singleLine = linhas == 1,
        minLines = linhas,
        isError = erro,
        modifier = modifier,
        colors = FormFieldDefaults.textFieldColors()
    )
}

/** Campo de nome com sugestões do cadastro; ao escolher, [onEscolher] recebe o funcionário. */
@Composable
private fun CampoColaborador(
    rotulo: String,
    valor: String,
    funcionarios: List<Funcionario>,
    onValor: (String) -> Unit,
    modifier: Modifier = Modifier.fillMaxWidth(),
    onEscolher: (Funcionario) -> Unit
) {
    Column(modifier) {
        Campo(rotulo, valor, Modifier.fillMaxWidth(), onValor = onValor)
        val q = valor.trim()
        val jaEscolhido = funcionarios.any { q.startsWith(it.nome, ignoreCase = true) }
        val achados = if (q.length >= 2 && !jaEscolhido) filtrarFuncionarios(funcionarios, q).take(5) else emptyList()
        ListaSugestoes(achados, onEscolher)
    }
}

@Composable
private fun BuscaParaAdicionar(rotulo: String, funcionarios: List<Funcionario>, onEscolher: (Funcionario) -> Unit) {
    var busca by remember { mutableStateOf("") }
    Column(Modifier.fillMaxWidth()) {
        Campo(rotulo, busca, onValor = { busca = it })
        val achados = if (busca.trim().length >= 2) filtrarFuncionarios(funcionarios, busca).take(6) else emptyList()
        ListaSugestoes(achados) { f ->
            busca = ""
            onEscolher(f)
        }
    }
}

@Composable
private fun ListaSugestoes(achados: List<Funcionario>, onEscolher: (Funcionario) -> Unit) {
    if (achados.isEmpty()) return
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.secondaryContainer)
    ) {
        achados.forEachIndexed { i, f ->
            if (i > 0) HorizontalDivider()
            Column(
                Modifier
                    .fillMaxWidth()
                    .clickable { onEscolher(f) }
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            ) {
                Text(f.nomeComVinculo, fontWeight = FontWeight.SemiBold)
                val det = listOfNotNull(
                    f.matricula.takeIf { it.isNotBlank() }?.let { "Matrícula $it" },
                    f.cargoNome.takeIf { it.isNotBlank() }
                )
                if (det.isNotEmpty()) Text(det.joinToString(" • "), style = MaterialTheme.typography.bodySmall)
            }
        }
    }
}

/** Texto livre separado por "; " com atalhos para os itens da Matriz. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ListaItens(rotulo: String, valor: String, opcoes: List<String>, onValor: (String) -> Unit) {
    Campo(rotulo, valor, linhas = 2, onValor = onValor)
    FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        opcoes.forEach { item ->
            FilterChip(
                selected = AprCatalog.contem(valor, item),
                onClick = { onValor(AprCatalog.alternar(valor, item)) },
                label = { Text(item, style = MaterialTheme.typography.labelSmall) }
            )
        }
    }
}
